// The engine output as the components read it, with the HTML markup defaults while it is not available yet
// (catalog still loading).
import { useMemo } from "react";
import useI18n from "../../../hooks/useTranslations";
import {
  createUiState,
  type CalcState,
  type ControlValues,
  type EngineOptions,
  type EngineOutput,
  type MaxRowsCols,
  type MaxScrewsInfo,
  type UiState,
} from "../../../http/screwConnector/formulas";

const EMPTY_OPTIONS: EngineOptions = {
  matTipo: { 1: [], 2: [] },
  matSpecie: { 1: [], 2: [] },
  matClasse: { 1: [], 2: [] },
  families: [],
  diameters: [],
  lengths: [],
  washers: [],
  cltDisposizione: { 1: [], 2: [] },
  cltLato: { 1: ["narrow", "wide"], 2: ["narrow", "wide"] },
  cltClassi: [],
};

export type EngineView = {
  ui: UiState;
  options: EngineOptions;
  // effective control values (what the HTML DOM shows after render); null without engine output
  values: ControlValues | null;
  maxRowsCols: MaxRowsCols | null;
  maxScrews: MaxScrewsInfo | null;
  state: CalcState | null;
  blockReason: string | null;
};

export function useEngineView(engine: EngineOutput | null): EngineView {
  const texts = useI18n().translation.screwConnectorModule.calculation;
  const defaultUi = useMemo(() => createUiState(texts), [texts]);
  return useMemo<EngineView>(
    () =>
      engine
        ? {
            ui: engine.ui,
            options: engine.options,
            values: engine.values,
            maxRowsCols: engine.maxRowsCols,
            maxScrews: engine.maxScrews,
            state: engine.state,
            blockReason: engine.blockReason,
          }
        : {
            ui: defaultUi,
            options: EMPTY_OPTIONS,
            values: null,
            maxRowsCols: null,
            maxScrews: null,
            state: null,
            blockReason: null,
          },
    [engine, defaultUi],
  );
}

// NaN / undefined -> null (a number control shown empty)
export const numOrNull = (v: number | null | undefined): number | null =>
  v === null || v === undefined || Number.isNaN(v) ? null : v;

export type SelectOption = { value: string; label: string };

export const stringOptions = (list: readonly string[]): SelectOption[] => list.map((v) => ({ value: v, label: v }));
